import { Helmet } from 'react-helmet-async';
import { Heart, Award, Users, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import getCanonicalUrl from '../utils/canonical';

const ChiSiamo = () => {
  const values = [
    {
      icon: Heart,
      title: 'Ascolto',
      description: 'Ogni persona ha una storia unica. Noi la ascoltiamo con attenzione e rispetto.'
    },
    {
      icon: Shield,
      title: 'Discrezione',
      description: 'La privacy è sacra. I vostri dati e le vostre ricerche rimangono riservati.'
    },
    {
      icon: Award,
      title: 'Esperienza',
      description: '19 anni di attività ci hanno insegnato cosa funziona davvero negli incontri.'
    },
    {
      icon: Users,
      title: 'Selezione',
      description: 'Non accettiamo tutti. Scegliamo persone serie, motivate e autentiche.'
    }
  ];

  return (
    <div className="page-container">
      <Helmet>
        <title>La Storia | Agenzia per Single a Napoli | Officine del Cuore</title>
        <meta name="description" content="Chi siamo: Officine del Cuore migliore agenzia matrimoniale per incontri seri: esperienza, riservatezza e incontri in sicurezza per relazioni serie e stabili" />
        <link rel="canonical" href={getCanonicalUrl('chi-siamo')} />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero chi-siamo-hero" style={{backgroundImage: 'url(https://images.unsplash.com/photo-1761569064977-f7bc70980478)'}}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="page-title fade-in">La Nostra Storia</h1>
          <p className="page-subtitle fade-in-delay">
            La storia di un'agenzia che crede nell'amore autentico
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-elegant">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-8">
            Scopri Chi Siamo
          </h2>
          <div className="prose-elegant">
            <p>
              <strong>Officine del Cuore</strong> nasce nel 2007 a Napoli da un'idea semplice ma potente: 
              restituire dignità e valore agli incontri tra persone che cercano l'amore vero.
            </p>
            <p>
              In un mondo sempre più digitale e frettoloso, abbiamo scelto di rimanere fedeli a un principio: 
              <em>l'amore non si trova con un click, ma si costruisce con pazienza, ascolto e rispetto</em>.
            </p>
            <p>
              Da 19 anni accompagniamo uomini e donne che hanno scelto di non affidarsi al caso o agli algoritmi, 
              ma a professionisti che conoscono l'importanza delle sfumature, della compatibilità vera e del valore umano.
            </p>
            <p>
              Con sedi a <strong>Napoli, Caserta</strong> e <strong>Roma</strong>, serviamo tutta la Campania e il Lazio, 
              ma collaboriamo anche con clienti in tutta Italia che desiderano il nostro metodo.
            </p>
          </div>
        </div>
      </section>

      {/* Difference Section */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-8">
            Cosa Ci Rende Diversi
          </h2>
          <div className="prose-elegant">
            <h3 className="text-burgundy font-playfair text-2xl mb-4">
              Non Siamo un'App. Siamo un'Agenzia Matrimoniale.
            </h3>
            <p>
              Le app di dating offrono quantità. Noi offriamo qualità.<br />
              Le app usano algoritmi. Noi usiamo l'empatia e l'esperienza umana.<br />
              Le app creano match. Noi creiamo incontri significativi.
            </p>
            <h3 className="text-burgundy font-playfair text-2xl mb-4 mt-8">
              Selezione Accurata
            </h3>
            <p>
              Non accettiamo chiunque. Prima di iscriversi, ogni persona viene intervistata per comprendere 
              motivazioni, aspettative e serietà. Solo chi è davvero pronto per una relazione stabile entra 
              nella nostra community.
            </p>
            <h3 className="text-burgundy font-playfair text-2xl mb-4 mt-8">
              Accompagnamento Continuo
            </h3>
            <p>
              Non vi lasciamo soli. Dopo ogni presentazione, vi seguiamo, raccogliamo feedback e affiniamo 
              la ricerca. Siamo al vostro fianco fino a quando non trovate la persona giusta.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-elegant">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="section-title text-center mb-12">
            I Nostri Valori
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => (
              <Card key={value.title} className="value-card">
                <CardContent className="p-6 text-center">
                  <div className="value-icon-wrapper">
                    <value.icon className="value-icon" />
                  </div>
                  <h3 className="value-title">{value.title}</h3>
                  <p className="value-description">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="section-title mb-8">
            Il Nostro Team
          </h2>
          <div className="prose-elegant">
            <p>
              Il team di Officine del Cuore è composto da professionisti con esperienza pluriennale 
              in psicologia delle relazioni, counseling e coaching. Ogni consulente è formato per ascoltare, 
              comprendere e guidare con sensibilità ed empatia.
            </p>
            <p>
              Non siamo venditori. Siamo facilitatori di incontri autentici.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action Button Section */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://officinedelcuore.com/lavora-con-noi" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="btn-primary btn-large">
                Lavora con Noi
              </Button>
            </a>
            <a href="tel:+393292626152">
              <Button size="lg" className="btn-primary btn-large">
                Chiama Ora
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="section-elegant">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="section-title text-center mb-12">
            Le Nostre Sedi
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="location-card">
              <CardContent className="p-8">
                <h3 className="location-title">Napoli</h3>
                <p className="location-description">
                  La nostra sede storica, nel cuore della città partenopea. 
                  Serviamo Napoli e tutta la Campania.
                </p>
                <div className="location-address">
                  <p className="font-medium">Via G. Testa, 16</p>
                  <p>Napoli (NA)</p>
                  <p className="mt-2">Tel.: +39 3292626152</p>
                  <p>info@officinedelcuore.it</p>
                </div>
              </CardContent>
            </Card>
            <Card className="location-card">
              <CardContent className="p-8">
                <h3 className="location-title">Caserta</h3>
                <p className="location-description">
                  Il nostro punto di riferimento a Caserta e provincia.
                </p>
                <div className="location-address">
                  <p className="font-medium">Viale Vincenzo Cappiello, 8</p>
                  <p>Caserta (CE)</p>
                  <p className="mt-2">Tel.: +39 3520567001</p>
                  <p>info@officinedelcuore.it</p>
                </div>
              </CardContent>
            </Card>
            <Card className="location-card">
              <CardContent className="p-8">
                <h3 className="location-title">Roma</h3>
                <p className="location-description">
                  La nostra sede nella Capitale, per servire Roma e tutto il Lazio 
                  con la stessa qualità e professionalità.
                </p>
                <div className="location-address">
                  <p className="font-medium">Via Mattia Battistini, 466a - Roma</p>
                  <p className="mt-2">Telefono/WhatsApp: +39 3455006556</p>
                  <p>info@officinedelcuore.it</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChiSiamo;
